import type {TFunction} from 'i18next';
import {UserRole, UserRoleType} from '@/config/roles';

export type TargetResolver = () => HTMLElement | null;

export type TutorialMode = 'full' | 'page';

/**
 * Popover placement relative to the spotlight target — or `pinned-bottom` to anchor the
 * card at the viewport bottom regardless of target position. Defaults to auto-placement.
 */
export type PopoverPlacement = 'right' | 'left' | 'top' | 'bottom' | 'pinned-bottom';

export type TutorialStep = {
    id: string;
    /**
     * Platform-relative path of the page this step belongs to (must match the pathname
     * with the leading `/:platform` segment stripped — see `toRelativePath`). Home is '/'.
     */
    page: string;
    /**
     * Which tour modes include this step. Defaults to both ['full', 'page'].
     * - 'full' = app-wide overview tour (orientation, header, etc.)
     * - 'page' = per-page tour (page-specific actions, library features)
     */
    modes?: TutorialMode[];
    /** CSS selector, runtime resolver, or null for a centered/banner step. */
    targetSelector: string | TargetResolver | null;
    title: string;
    content: string;
    /** Hint shown when the user is on the wrong page (e.g. "Open the Trends page"). */
    navigateHint: string;
    /** Renders as a non-blocking banner so the user can interact with the page. */
    nonBlocking?: boolean;
    /** Force a popover side; defaults to auto-placement based on available viewport space. */
    placement?: PopoverPlacement;
    /** Restrict step to users holding any of these roles. Empty/undefined = all users. */
    roles?: UserRoleType[];
    /**
     * The page-tour this step belongs to (normally the hub/entry route, e.g. '/statistics').
     * Steps that live on a subpage but belong to a hub walkthrough set this to the hub path.
     * Defaults to `page` (single-page tours need not set it).
     */
    tour?: string;
};

/**
 * Single source of truth for routes that participate in the tutorial.
 * Drives `getCurrentPageLabel`, `hasPageTutorialFor`, and the wrong-page "Go to X" button.
 *
 * REMUS routes are prefixed with `/:platform` (PROD/TEST). Paths here are stored WITHOUT
 * that prefix — the leading platform segment is stripped at match time via `toRelativePath`,
 * and re-attached for navigation via `getNavTarget`.
 */
export type TutorialPage = {path: string; labelKey: string};

/**
 * Pipeline editor entity route — ids are dynamic, so the page is a pattern path.
 * Pattern tours can only start from the page itself (the tour never navigates to them).
 */
export const PIPELINE_DETAILS_PAGE =
    '/reporting/measurementsProcessing/clusters/:clusterVersionId/pipeline/:pipelineId?';

export const TUTORIAL_PAGES: TutorialPage[] = [
    {path: '/', labelKey: 'nav.home'},
    {path: '/trends', labelKey: 'nav.trends'},
    {path: '/alarms', labelKey: 'nav.alarms'},
    {path: '/dashboards', labelKey: 'nav.dashboards'},
    {path: '/statistics', labelKey: 'nav.statistics'},
    {path: '/reporting', labelKey: 'nav.reporting'},
    {path: PIPELINE_DETAILS_PAGE, labelKey: 'nav.pipelineDetails'},
    {path: '/notifications', labelKey: 'nav.notifications'},
    {path: '/monitoringGroups', labelKey: 'nav.monitoringGroups'},
    {path: '/applicationEditor', labelKey: 'nav.applicationEditor'},
    {path: '/admin', labelKey: 'nav.admin'},
];

/** True when a page path contains `:param` segments (entity routes with dynamic ids). */
export function isPatternPath(path: string): boolean {
    return path.includes('/:');
}

/**
 * Match a step/page path against a concrete platform-relative path. Plain paths compare
 * exactly; pattern paths match segment-wise — `:param` matches any single segment and a
 * trailing `:param?` segment is optional.
 */
export function pathMatchesPage(pagePath: string, relativePath: string): boolean {
    if (!isPatternPath(pagePath)) return pagePath === relativePath;
    const patternSegs = pagePath.split('/').filter(Boolean);
    const pathSegs = relativePath.split('/').filter(Boolean);
    if (pathSegs.length > patternSegs.length) return false;
    return patternSegs.every((seg, i) => {
        const value = pathSegs[i];
        if (value === undefined) return seg.startsWith(':') && seg.endsWith('?');
        if (seg.startsWith(':')) return true;
        return seg === value;
    });
}

/**
 * Strip the leading `/:platform` segment so steps and TUTORIAL_PAGES can use
 * platform-agnostic paths. `/PROD` -> '/', `/PROD/trends` -> '/trends'.
 */
export function toRelativePath(pathname: string, platform: string | null | undefined): string {
    if (!platform) return pathname || '/';
    const prefix = `/${platform}`;
    if (pathname === prefix) return '/';
    if (pathname.startsWith(`${prefix}/`)) return pathname.slice(prefix.length);
    return pathname || '/';
}

/** Spotlight resolver for the primary nav (sidebar on desktop, bottom-nav on mobile). */
export const primaryNavResolver: TargetResolver = () => {
    const candidates: Array<HTMLElement | null> = [
        document.querySelector('[data-tutorial="bottom-nav"]'),
        document.querySelector('[data-tutorial="sidebar"]'),
        document.querySelector('[data-tutorial="app-header"]'),
    ];
    for (const el of candidates) {
        if (!el) continue;
        const style = getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') continue;
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) return el;
    }
    return null;
};

/** Runtime resolvers for elements rendered by @cern/hse-filtering-library (no stable selectors). */
const findButtonByText = (text: string): HTMLElement | null => {
    const btn = Array.from(document.querySelectorAll('button')).find(
        (b) => (b.textContent || '').trim() === text
    );
    return (btn as HTMLElement | undefined) ?? null;
};

/**
 * Find the smallest element whose first child is a TEXT node beginning with `labelPrefix`.
 * Filtering for TEXT nodes (not just any firstChild) avoids matching outer wrappers whose
 * first DIV child happens to start with the same text.
 */
const findLabeledControl = (labelPrefix: string): HTMLElement | null => {
    const labels = Array.from(document.querySelectorAll('div, span, label, p'));
    for (const el of labels) {
        const firstChild = el.firstChild;
        if (!firstChild || firstChild.nodeType !== Node.TEXT_NODE) continue;
        const text = (firstChild.textContent || '').trim();
        if (text.startsWith(labelPrefix)) {
            return el as HTMLElement;
        }
    }
    return null;
};

/**
 * Find the MUI dropdown inside a labeled control wrapper, so the spotlight points at the
 * input rather than the label + input together.
 */
const findDropdownByLabel = (labelPrefix: string): HTMLElement | null => {
    const wrapper = findLabeledControl(labelPrefix);
    if (!wrapper) return null;
    const dropdown = wrapper.querySelector(
        '.MuiAutocomplete-root, .MuiOutlinedInput-root, .MuiSelect-root, [role="combobox"]'
    );
    return (dropdown as HTMLElement | null) ?? wrapper;
};

const filteringLibraryResolvers = (t: TFunction) => ({
    /**
     * Filter-icon button on a text column (preferred for a meaningful filter demo).
     * Falls back to the first filter button if no preferred text column is found.
     */
    gridFilterIcon: (): HTMLElement | null => {
        const preferredColIds = ['locationName', 'ouvrageLocal', 'name', 'sigle', 'description'];
        for (const id of preferredColIds) {
            const btn = document.querySelector(
                `[col-id="${id}"] .ag-header-cell-filter-button`
            ) as HTMLElement | null;
            if (btn) return btn;
        }
        return document.querySelector('.ag-header-cell-filter-button') as HTMLElement | null;
    },
    /** "Statistics" toggle button (one of List/Statistics/Map). */
    sectionsToggle: (): HTMLElement | null =>
        findButtonByText(t('filtering_library.sections.statistics_section')),
    /** "Group by" dropdown (the input itself, not the label). */
    groupBy: (): HTMLElement | null => findDropdownByLabel(t('filtering_library.group_by')),
    /** "Compute Statistics" dropdown (the input itself, not the label). */
    computeStat: (): HTMLElement | null =>
        findDropdownByLabel(t('filtering_library.compute_stat')),
    /**
     * Map section: the library renders MapVisualisation as a React fragment containing a
     * heading sibling and the map div sibling, so the heading's parent is the entire
     * FilteringPage. Find the heading and spotlight its next sibling (the actual map div).
     */
    mapSection: (): HTMLElement | null => {
        const headings = Array.from(document.querySelectorAll('h1, h2, h3, h4, h5, h6'));
        const heading = headings.find((el) => /^map\b/i.test((el.textContent || '').trim()));
        return (heading?.nextElementSibling as HTMLElement | null) ?? null;
    },
    /** "Favorites" button (yellow star). */
    favorites: (): HTMLElement | null => findButtonByText(t('filtering_library.favorites')),
    /** "Reset all filters" toolbar button. */
    resetFilters: (): HTMLElement | null =>
        findButtonByText(t('filtering_library.reset_filters')),
    /** First data row in the AG Grid (for "click a row" demo). */
    firstRow: (): HTMLElement | null =>
        document.querySelector('.ag-row[row-index="0"]') as HTMLElement | null,
    /** AG Grid root (the main data grid; first `.ag-root-wrapper` on the page). */
    gridRoot: (): HTMLElement | null =>
        document.querySelector('.ag-root-wrapper') as HTMLElement | null,
    /** "Show" metric dropdown inside the chart panel (visible only after grouping). */
    metricSelector: (): HTMLElement | null =>
        findDropdownByLabel(t('filtering_library.show')),
    /** "Table" toggle inside the chart-section visibility control (chart vs table view). */
    chartTableToggle: (): HTMLElement | null =>
        findButtonByText(t('filtering_library.sections.grid_section')),
    /** "Excel" toolbar button on the main grid. */
    excelButton: (): HTMLElement | null => findButtonByText(t('filtering_library.excel')),
});

/** Shared filtering-library resolvers, exposed for per-page step arrays. */
export type FilteringResolvers = ReturnType<typeof filteringLibraryResolvers>;

export function getTutorialSteps(t: TFunction): TutorialStep[] {
    // Filtering-library resolvers for grid controls the library renders at runtime
    // (filter icon, reset, section toggle, map, export) — no stable selectors, so resolved live.
    const fl: FilteringResolvers = filteringLibraryResolvers(t);

    // ── Overview tour: orientation only (full mode) ────────────────────────────
    const overview: TutorialStep[] = [
        {
            id: 'welcome',
            page: '/',
            targetSelector: null,
            title: t('tutorial.welcome.title'),
            content: t('tutorial.welcome.body'),
            navigateHint: t('tutorial.welcome.navigateHint'),
            modes: ['full'],
        },
        {
            id: 'sidebar',
            page: '/',
            targetSelector: primaryNavResolver,
            title: t('tutorial.sidebar.title'),
            content: t('tutorial.sidebar.body'),
            navigateHint: t('tutorial.sidebar.navigateHint'),
            modes: ['full'],
        },
        {
            id: 'platform',
            page: '/',
            targetSelector: '[data-tutorial="platform-switcher"]',
            title: t('tutorial.platform.title'),
            content: t('tutorial.platform.body'),
            navigateHint: t('tutorial.platform.navigateHint'),
            modes: ['full'],
        },
        {
            id: 'docs',
            page: '/',
            targetSelector: '[data-tutorial="docs-menu"]',
            title: t('tutorial.docs.title'),
            content: t('tutorial.docs.body'),
            navigateHint: t('tutorial.docs.navigateHint'),
            modes: ['full'],
        },
        {
            id: 'user-menu',
            page: '/',
            targetSelector: '[data-tutorial="user-menu"]',
            title: t('tutorial.userMenu.title'),
            content: t('tutorial.userMenu.body'),
            navigateHint: t('tutorial.userMenu.navigateHint'),
            modes: ['full'],
        },
        {
            id: 'overview-trends',
            page: '/trends',
            targetSelector: '.trends-page',
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.overviewTrends.title'),
            content: t('tutorial.overviewTrends.body'),
            navigateHint: t('tutorial.overviewTrends.navigateHint'),
            modes: ['full'],
        },
        {
            id: 'overview-alarms',
            page: '/alarms',
            targetSelector: fl.gridRoot,
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.overviewAlarms.title'),
            content: t('tutorial.overviewAlarms.body'),
            navigateHint: t('tutorial.overviewAlarms.navigateHint'),
            modes: ['full'],
        },
        {
            id: 'overview-dashboards',
            page: '/dashboards',
            targetSelector: '[data-tutorial="dashboards-root"]',
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.overviewDashboards.title'),
            content: t('tutorial.overviewDashboards.body'),
            navigateHint: t('tutorial.overviewDashboards.navigateHint'),
            modes: ['full'],
        },
        {
            id: 'done',
            page: '/',
            targetSelector: null,
            title: t('tutorial.done.title'),
            content: t('tutorial.done.body'),
            navigateHint: t('tutorial.done.navigateHint'),
            modes: ['full'],
        },
    ];

    // ── Per-page steps: page-specific actions + library features (page mode) ───
    // Library features (filter, row-click, reset, sections…) are duplicated per page because
    // each page configures its own visibleSections and column types.

    const alarmsSteps: TutorialStep[] = [
        {
            id: 'alarms-intro',
            page: '/alarms',
            modes: ['page'],
            targetSelector: fl.gridRoot,
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.alarmsIntro.title'),
            content: t('tutorial.alarmsIntro.body'),
            navigateHint: t('tutorial.alarmsIntro.navigateHint'),
        },
        {
            id: 'alarms-row-click',
            page: '/alarms',
            modes: ['page'],
            targetSelector: fl.firstRow,
            nonBlocking: true,
            // Page-specific copy: clicking an alarm row navigates to the MS dashboard,
            // which would end the tour — the text must not invite a click mid-tour.
            title: t('tutorial.alarmsRowClick.title'),
            content: t('tutorial.alarmsRowClick.body'),
            navigateHint: t('tutorial.alarmsRowClick.navigateHint'),
        },
        {
            id: 'alarms-grid-filter',
            page: '/alarms',
            modes: ['page'],
            targetSelector: fl.gridFilterIcon,
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.gridFilter.title'),
            content: t('tutorial.gridFilter.body'),
            navigateHint: t('tutorial.gridFilter.navigateHint'),
        },
        {
            id: 'alarms-sources',
            page: '/alarms',
            modes: ['page'],
            targetSelector: '[data-tutorial="alarms-sources-bar"]',
            nonBlocking: true,
            title: t('tutorial.alarmsSources.title'),
            content: t('tutorial.alarmsSources.body'),
            navigateHint: t('tutorial.alarmsSources.navigateHint'),
        },
        {
            id: 'alarms-reset-all',
            page: '/alarms',
            modes: ['page'],
            targetSelector: '[data-tutorial="alarms-reset-all"]',
            nonBlocking: true,
            title: t('tutorial.alarmsReset.title'),
            content: t('tutorial.alarmsReset.body'),
            navigateHint: t('tutorial.alarmsReset.navigateHint'),
        },
        {
            id: 'alarms-save-view',
            page: '/alarms',
            modes: ['page'],
            targetSelector: '[data-tutorial="alarms-save-view"]',
            nonBlocking: true,
            title: t('tutorial.alarmsSaveView.title'),
            content: t('tutorial.alarmsSaveView.body'),
            navigateHint: t('tutorial.alarmsSaveView.navigateHint'),
        },
        {
            id: 'alarms-load-view',
            page: '/alarms',
            modes: ['page'],
            targetSelector: '[data-tutorial="alarms-load-view"]',
            nonBlocking: true,
            title: t('tutorial.alarmsLoadView.title'),
            content: t('tutorial.alarmsLoadView.body'),
            navigateHint: t('tutorial.alarmsLoadView.navigateHint'),
        },
        {
            id: 'alarms-sections',
            page: '/alarms',
            modes: ['page'],
            targetSelector: fl.sectionsToggle,
            nonBlocking: true,
            title: t('tutorial.gridSections.title'),
            content: t('tutorial.gridSections.body'),
            navigateHint: t('tutorial.gridSections.navigateHint'),
        },
        {
            id: 'alarms-export',
            page: '/alarms',
            modes: ['page'],
            targetSelector: fl.excelButton,
            nonBlocking: true,
            title: t('tutorial.gridExport.title'),
            content: t('tutorial.gridExport.body'),
            navigateHint: t('tutorial.gridExport.navigateHint'),
        },
    ];

    const trendsSteps: TutorialStep[] = [
        {
            id: 'trends-intro',
            page: '/trends',
            modes: ['page'],
            targetSelector: '.trends-page',
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.trendsIntro.title'),
            content: t('tutorial.trendsIntro.body'),
            navigateHint: t('tutorial.trendsIntro.navigateHint'),
        },
        {
            id: 'trends-form',
            page: '/trends',
            modes: ['page'],
            targetSelector: '.trends-page-form-card',
            placement: 'right',
            nonBlocking: true,
            title: t('tutorial.trendsForm.title'),
            content: t('tutorial.trendsForm.body'),
            navigateHint: t('tutorial.trendsForm.navigateHint'),
        },
        {
            id: 'trends-view-mode',
            page: '/trends',
            modes: ['page'],
            targetSelector: '.trends-page-view-mode',
            placement: 'bottom',
            nonBlocking: true,
            title: t('tutorial.trendsViewMode.title'),
            content: t('tutorial.trendsViewMode.body'),
            navigateHint: t('tutorial.trendsViewMode.navigateHint'),
        },
        {
            id: 'trends-chart',
            page: '/trends',
            modes: ['page'],
            targetSelector: '.trends-page-chart-card',
            placement: 'bottom',
            nonBlocking: true,
            title: t('tutorial.trendsChart.title'),
            content: t('tutorial.trendsChart.body'),
            navigateHint: t('tutorial.trendsChart.navigateHint'),
        },
        {
            id: 'trends-series-grid',
            page: '/trends',
            modes: ['page'],
            targetSelector: '.trends-page-grid-card',
            placement: 'top',
            nonBlocking: true,
            title: t('tutorial.trendsSeriesGrid.title'),
            content: t('tutorial.trendsSeriesGrid.body'),
            navigateHint: t('tutorial.trendsSeriesGrid.navigateHint'),
        },
        {
            id: 'trends-export',
            page: '/trends',
            modes: ['page'],
            targetSelector: '[data-tutorial="trends-export"]',
            nonBlocking: true,
            title: t('tutorial.trendsExport.title'),
            content: t('tutorial.trendsExport.body'),
            navigateHint: t('tutorial.trendsExport.navigateHint'),
        },
        {
            id: 'trends-share',
            page: '/trends',
            modes: ['page'],
            targetSelector: '[data-tutorial="trends-share"]',
            placement: 'bottom',
            nonBlocking: true,
            title: t('tutorial.trendsShare.title'),
            content: t('tutorial.trendsShare.body'),
            navigateHint: t('tutorial.trendsShare.navigateHint'),
        },
        {
            id: 'trends-heatmap-tab',
            page: '/trends',
            modes: ['page'],
            targetSelector: '.trends-page-bottom-tabs-card',
            placement: 'top',
            nonBlocking: true,
            title: t('tutorial.trendsHeatmapTab.title'),
            content: t('tutorial.trendsHeatmapTab.body'),
            navigateHint: t('tutorial.trendsHeatmapTab.navigateHint'),
        },
    ];

    const notificationsSteps: TutorialStep[] = [
        {
            id: 'notifications-intro',
            page: '/notifications',
            modes: ['page'],
            targetSelector: fl.gridRoot,
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.notificationsIntro.title'),
            content: t('tutorial.notificationsIntro.body'),
            navigateHint: t('tutorial.notificationsIntro.navigateHint'),
        },
        {
            id: 'notifications-row-click',
            page: '/notifications',
            modes: ['page'],
            targetSelector: fl.firstRow,
            nonBlocking: true,
            // Page-specific copy: clicking a row navigates to the notification editor,
            // which would end the tour — the text must not invite a click mid-tour.
            title: t('tutorial.notificationsRowClick.title'),
            content: t('tutorial.notificationsRowClick.body'),
            navigateHint: t('tutorial.notificationsRowClick.navigateHint'),
        },
        {
            id: 'notifications-filter',
            page: '/notifications',
            modes: ['page'],
            targetSelector: fl.gridFilterIcon,
            nonBlocking: true,
            title: t('tutorial.gridFilter.title'),
            content: t('tutorial.gridFilter.body'),
            navigateHint: t('tutorial.gridFilter.navigateHint'),
        },
        {
            id: 'notifications-reset-filters',
            page: '/notifications',
            modes: ['page'],
            targetSelector: fl.resetFilters,
            nonBlocking: true,
            title: t('tutorial.resetFilters.title'),
            content: t('tutorial.resetFilters.body'),
            navigateHint: t('tutorial.resetFilters.navigateHint'),
        },
        {
            id: 'notifications-actions',
            page: '/notifications',
            modes: ['page'],
            targetSelector: '[data-tutorial="notifications-actions"]',
            nonBlocking: true,
            title: t('tutorial.notificationsActions.title'),
            content: t('tutorial.notificationsActions.body'),
            navigateHint: t('tutorial.notificationsActions.navigateHint'),
        },
        {
            id: 'notifications-new',
            page: '/notifications',
            modes: ['page'],
            targetSelector: '[data-tutorial="notifications-create"]',
            placement: 'bottom',
            nonBlocking: true,
            title: t('tutorial.notificationsNew.title'),
            content: t('tutorial.notificationsNew.body'),
            navigateHint: t('tutorial.notificationsNew.navigateHint'),
        },
        {
            id: 'notifications-show-all',
            page: '/notifications',
            modes: ['page'],
            targetSelector: '[data-tutorial="notifications-show-all"]',
            nonBlocking: true,
            roles: [UserRole.ADMIN],
            title: t('tutorial.notificationsShowAll.title'),
            content: t('tutorial.notificationsShowAll.body'),
            navigateHint: t('tutorial.notificationsShowAll.navigateHint'),
        },
    ];

    const monitoringGroupsSteps: TutorialStep[] = [
        {
            id: 'monitoring-groups-intro',
            page: '/monitoringGroups',
            modes: ['page'],
            targetSelector: fl.gridRoot,
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.monitoringGroupsIntro.title'),
            content: t('tutorial.monitoringGroupsIntro.body'),
            navigateHint: t('tutorial.monitoringGroupsIntro.navigateHint'),
        },
        {
            id: 'monitoring-groups-row-click',
            page: '/monitoringGroups',
            modes: ['page'],
            targetSelector: fl.firstRow,
            nonBlocking: true,
            // Page-specific copy: clicking a row opens the in-page group editor, so the
            // text actively invites it (the channels/save steps need an open editor).
            title: t('tutorial.monitoringGroupsRowClick.title'),
            content: t('tutorial.monitoringGroupsRowClick.body'),
            navigateHint: t('tutorial.monitoringGroupsRowClick.navigateHint'),
        },
        {
            id: 'monitoring-groups-public-filter',
            page: '/monitoringGroups',
            modes: ['page'],
            targetSelector: '[data-tutorial="monitoring-groups-public-filter"]',
            nonBlocking: true,
            title: t('tutorial.monitoringGroupsPublicFilter.title'),
            content: t('tutorial.monitoringGroupsPublicFilter.body'),
            navigateHint: t('tutorial.monitoringGroupsPublicFilter.navigateHint'),
        },
        {
            id: 'monitoring-groups-new',
            page: '/monitoringGroups',
            modes: ['page'],
            targetSelector: '[data-tutorial="monitoring-groups-new"]',
            nonBlocking: true,
            title: t('tutorial.monitoringGroupsNew.title'),
            content: t('tutorial.monitoringGroupsNew.body'),
            navigateHint: t('tutorial.monitoringGroupsNew.navigateHint'),
        },
        {
            id: 'monitoring-groups-channels',
            page: '/monitoringGroups',
            modes: ['page'],
            targetSelector: '[data-tutorial="monitoring-groups-channels"]',
            nonBlocking: true,
            title: t('tutorial.monitoringGroupsChannels.title'),
            content: t('tutorial.monitoringGroupsChannels.body'),
            navigateHint: t('tutorial.monitoringGroupsChannels.navigateHint'),
        },
        {
            id: 'monitoring-groups-save',
            page: '/monitoringGroups',
            modes: ['page'],
            targetSelector: '[data-tutorial="monitoring-groups-save"]',
            nonBlocking: true,
            title: t('tutorial.monitoringGroupsSave.title'),
            content: t('tutorial.monitoringGroupsSave.body'),
            navigateHint: t('tutorial.monitoringGroupsSave.navigateHint'),
        },
    ];

    const dashboardsSteps: TutorialStep[] = [
        {
            id: 'dashboards-intro',
            page: '/dashboards',
            modes: ['page'],
            targetSelector: '[data-tutorial="dashboards-root"]',
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.dashboardsIntro.title'),
            content: t('tutorial.dashboardsIntro.body'),
            navigateHint: t('tutorial.dashboardsIntro.navigateHint'),
        },
        {
            id: 'dashboards-new',
            page: '/dashboards',
            modes: ['page'],
            targetSelector: '[data-tutorial="dashboards-new"]',
            placement: 'bottom',
            nonBlocking: true,
            title: t('tutorial.dashboardsNew.title'),
            content: t('tutorial.dashboardsNew.body'),
            navigateHint: t('tutorial.dashboardsNew.navigateHint'),
        },
        {
            id: 'dashboards-ms',
            page: '/dashboards',
            modes: ['page'],
            targetSelector: '[data-tutorial="dashboards-ms"]',
            placement: 'bottom',
            nonBlocking: true,
            title: t('tutorial.dashboardsMs.title'),
            content: t('tutorial.dashboardsMs.body'),
            navigateHint: t('tutorial.dashboardsMs.navigateHint'),
        },
        {
            id: 'dashboards-your',
            page: '/dashboards',
            modes: ['page'],
            targetSelector: '[data-tutorial="dashboards-your"]',
            placement: 'bottom',
            nonBlocking: true,
            title: t('tutorial.dashboardsYour.title'),
            content: t('tutorial.dashboardsYour.body'),
            navigateHint: t('tutorial.dashboardsYour.navigateHint'),
        },
        {
            id: 'dashboards-shared',
            page: '/dashboards',
            modes: ['page'],
            targetSelector: '[data-tutorial="dashboards-shared"]',
            placement: 'top',
            nonBlocking: true,
            title: t('tutorial.dashboardsShared.title'),
            content: t('tutorial.dashboardsShared.body'),
            navigateHint: t('tutorial.dashboardsShared.navigateHint'),
        },
        {
            id: 'dashboards-open-editor',
            page: '/dashboards/new',
            tour: '/dashboards',
            modes: ['page'],
            targetSelector: '.dashboard-container',
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.dashboardsOpenEditor.title'),
            content: t('tutorial.dashboardsOpenEditor.body'),
            navigateHint: t('tutorial.dashboardsOpenEditor.navigateHint'),
        },
    ];

    const statisticsSteps: TutorialStep[] = [
        {
            id: 'statistics-intro',
            page: '/statistics',
            tour: '/statistics',
            modes: ['page'],
            targetSelector: '.page-header',
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.statisticsIntro.title'),
            content: t('tutorial.statisticsIntro.body'),
            navigateHint: t('tutorial.statisticsIntro.navigateHint'),
        },
        {
            id: 'statistics-current',
            page: '/statistics',
            tour: '/statistics',
            modes: ['page'],
            targetSelector: '.tile-section--blue',
            placement: 'top',
            nonBlocking: true,
            title: t('tutorial.statisticsCurrent.title'),
            content: t('tutorial.statisticsCurrent.body'),
            navigateHint: t('tutorial.statisticsCurrent.navigateHint'),
        },
        {
            id: 'statistics-current-open',
            page: '/statistics/channels',
            tour: '/statistics',
            modes: ['page'],
            targetSelector: fl.gridRoot,
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.statisticsCurrentOpen.title'),
            content: t('tutorial.statisticsCurrentOpen.body'),
            navigateHint: t('tutorial.statisticsCurrentOpen.navigateHint'),
        },
        {
            id: 'statistics-current-filter',
            page: '/statistics/channels',
            tour: '/statistics',
            modes: ['page'],
            targetSelector: fl.gridFilterIcon,
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.gridFilter.title'),
            content: t('tutorial.gridFilter.body'),
            navigateHint: t('tutorial.gridFilter.navigateHint'),
        },
        {
            id: 'statistics-history',
            page: '/statistics',
            tour: '/statistics',
            modes: ['page'],
            targetSelector: '.tile-section--green',
            placement: 'top',
            nonBlocking: true,
            title: t('tutorial.statisticsHistory.title'),
            content: t('tutorial.statisticsHistory.body'),
            navigateHint: t('tutorial.statisticsHistory.navigateHint'),
        },
        {
            id: 'statistics-history-open',
            page: '/statistics/channelHistory',
            tour: '/statistics',
            modes: ['page'],
            targetSelector: fl.gridRoot,
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.statisticsHistoryOpen.title'),
            content: t('tutorial.statisticsHistoryOpen.body'),
            navigateHint: t('tutorial.statisticsHistoryOpen.navigateHint'),
        },
        {
            id: 'statistics-parameters',
            page: '/statistics',
            tour: '/statistics',
            modes: ['page'],
            targetSelector: '.tile-section--orange',
            placement: 'top',
            nonBlocking: true,
            title: t('tutorial.statisticsParameters.title'),
            content: t('tutorial.statisticsParameters.body'),
            navigateHint: t('tutorial.statisticsParameters.navigateHint'),
        },
        {
            id: 'statistics-parameters-open',
            page: '/statistics/latestParameters',
            tour: '/statistics',
            modes: ['page'],
            targetSelector: fl.gridRoot,
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.statisticsParametersOpen.title'),
            content: t('tutorial.statisticsParametersOpen.body'),
            navigateHint: t('tutorial.statisticsParametersOpen.navigateHint'),
        },
        {
            id: 'statistics-sessions',
            page: '/statistics',
            tour: '/statistics',
            modes: ['page'],
            targetSelector: '.tile-section--purple',
            placement: 'top',
            nonBlocking: true,
            roles: [UserRole.ADMIN, UserRole.EXPERT_EN, UserRole.EXPERT_RP],
            title: t('tutorial.statisticsSessions.title'),
            content: t('tutorial.statisticsSessions.body'),
            navigateHint: t('tutorial.statisticsSessions.navigateHint'),
        },
        {
            id: 'statistics-sessions-open',
            page: '/statistics/userPrivileges',
            tour: '/statistics',
            modes: ['page'],
            targetSelector: fl.gridRoot,
            placement: 'pinned-bottom',
            nonBlocking: true,
            roles: [UserRole.ADMIN, UserRole.EXPERT_EN, UserRole.EXPERT_RP],
            title: t('tutorial.statisticsSessionsOpen.title'),
            content: t('tutorial.statisticsSessionsOpen.body'),
            navigateHint: t('tutorial.statisticsSessionsOpen.navigateHint'),
        },
    ];

    const applicationEditorSteps: TutorialStep[] = [
        {
            id: 'application-editor-intro',
            page: '/applicationEditor',
            modes: ['page'],
            targetSelector: '.page-header',
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.applicationEditorIntro.title'),
            content: t('tutorial.applicationEditorIntro.body'),
            navigateHint: t('tutorial.applicationEditorIntro.navigateHint'),
        },
        {
            id: 'application-editor-catalog',
            page: '/applicationEditor',
            modes: ['page'],
            targetSelector: fl.gridRoot,
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.applicationEditorCatalog.title'),
            content: t('tutorial.applicationEditorCatalog.body'),
            navigateHint: t('tutorial.applicationEditorCatalog.navigateHint'),
        },
        {
            id: 'application-editor-open',
            page: '/applicationEditor',
            modes: ['page'],
            targetSelector: fl.firstRow,
            nonBlocking: true,
            title: t('tutorial.applicationEditorOpen.title'),
            content: t('tutorial.applicationEditorOpen.body'),
            navigateHint: t('tutorial.applicationEditorOpen.navigateHint'),
        },
        {
            id: 'application-editor-user-manual',
            page: '/applicationEditor',
            modes: ['page'],
            targetSelector: '[data-tutorial="application-editor-user-manual"]',
            placement: 'bottom',
            nonBlocking: true,
            title: t('tutorial.applicationEditorUserManual.title'),
            content: t('tutorial.applicationEditorUserManual.body'),
            navigateHint: t('tutorial.applicationEditorUserManual.navigateHint'),
        },
        {
            id: 'application-editor-manage-widgets',
            page: '/applicationEditor',
            modes: ['page'],
            targetSelector: '#manage-widgets',
            placement: 'right',
            nonBlocking: true,
            roles: [UserRole.ADMIN],
            title: t('tutorial.applicationEditorManageWidgets.title'),
            content: t('tutorial.applicationEditorManageWidgets.body'),
            navigateHint: t('tutorial.applicationEditorManageWidgets.navigateHint'),
        },
        {
            id: 'application-editor-manage-synoptics',
            page: '/applicationEditor',
            modes: ['page'],
            targetSelector: '#manage-synoptics',
            placement: 'right',
            nonBlocking: true,
            title: t('tutorial.applicationEditorManageSynoptics.title'),
            content: t('tutorial.applicationEditorManageSynoptics.body'),
            navigateHint: t('tutorial.applicationEditorManageSynoptics.navigateHint'),
        },
        {
            id: 'application-editor-widgets-open',
            page: '/applicationEditor/widgets',
            tour: '/applicationEditor',
            modes: ['page'],
            targetSelector: '.page-header',
            placement: 'pinned-bottom',
            nonBlocking: true,
            roles: [UserRole.ADMIN],
            title: t('tutorial.applicationEditorWidgetsOpen.title'),
            content: t('tutorial.applicationEditorWidgetsOpen.body'),
            navigateHint: t('tutorial.applicationEditorWidgetsOpen.navigateHint'),
        },
        {
            id: 'application-editor-synoptics-open',
            page: '/applicationEditor/synoptics',
            tour: '/applicationEditor',
            modes: ['page'],
            targetSelector: '.page-header',
            placement: 'pinned-bottom',
            nonBlocking: true,
            roles: [UserRole.ADMIN],
            title: t('tutorial.applicationEditorSynopticsOpen.title'),
            content: t('tutorial.applicationEditorSynopticsOpen.body'),
            navigateHint: t('tutorial.applicationEditorSynopticsOpen.navigateHint'),
        },
    ];

    const reportingSteps: TutorialStep[] = [
        {
            id: 'reporting-intro',
            page: '/reporting',
            tour: '/reporting',
            modes: ['page'],
            targetSelector: '.page-header',
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.reportingIntro.title'),
            content: t('tutorial.reportingIntro.body'),
            navigateHint: t('tutorial.reportingIntro.navigateHint'),
        },
        {
            id: 'reporting-standard',
            page: '/reporting',
            tour: '/reporting',
            modes: ['page'],
            targetSelector: '.tile-section--blue',
            placement: 'top',
            nonBlocking: true,
            title: t('tutorial.reportingStandard.title'),
            content: t('tutorial.reportingStandard.body'),
            navigateHint: t('tutorial.reportingStandard.navigateHint'),
        },
        {
            id: 'reporting-measurements-open',
            page: '/reporting/measurementsStatistics',
            tour: '/reporting',
            modes: ['page'],
            targetSelector: '.page-header',
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.reportingMeasurementsOpen.title'),
            content: t('tutorial.reportingMeasurementsOpen.body'),
            navigateHint: t('tutorial.reportingMeasurementsOpen.navigateHint'),
        },
        {
            id: 'reporting-alarm-extraction-open',
            page: '/reporting/alarmExtraction',
            tour: '/reporting',
            modes: ['page'],
            targetSelector: '.page-header',
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.reportingAlarmExtractionOpen.title'),
            content: t('tutorial.reportingAlarmExtractionOpen.body'),
            navigateHint: t('tutorial.reportingAlarmExtractionOpen.navigateHint'),
        },
        {
            id: 'reporting-availability-open',
            page: '/reporting/availability',
            tour: '/reporting',
            modes: ['page'],
            targetSelector: '.page-header',
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.reportingAvailabilityOpen.title'),
            content: t('tutorial.reportingAvailabilityOpen.body'),
            navigateHint: t('tutorial.reportingAvailabilityOpen.navigateHint'),
        },
        {
            id: 'reporting-snapshots-open',
            page: '/reporting/snapshots',
            tour: '/reporting',
            modes: ['page'],
            targetSelector: '.page-header',
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.reportingSnapshotsOpen.title'),
            content: t('tutorial.reportingSnapshotsOpen.body'),
            navigateHint: t('tutorial.reportingSnapshotsOpen.navigateHint'),
        },
        {
            id: 'reporting-expert',
            page: '/reporting',
            tour: '/reporting',
            modes: ['page'],
            targetSelector: '.tile-section--green',
            placement: 'top',
            nonBlocking: true,
            roles: [UserRole.ADMIN, UserRole.USER_EN, UserRole.EXPERT_EN],
            title: t('tutorial.reportingExpert.title'),
            content: t('tutorial.reportingExpert.body'),
            navigateHint: t('tutorial.reportingExpert.navigateHint'),
        },
        {
            id: 'reporting-meteo-open',
            page: '/reporting/meteo',
            tour: '/reporting',
            modes: ['page'],
            targetSelector: '.page-header',
            placement: 'pinned-bottom',
            nonBlocking: true,
            roles: [UserRole.ADMIN, UserRole.EXPERT_EN, UserRole.EXPERT_RP],
            title: t('tutorial.reportingMeteoOpen.title'),
            content: t('tutorial.reportingMeteoOpen.body'),
            navigateHint: t('tutorial.reportingMeteoOpen.navigateHint'),
        },
        {
            id: 'reporting-hyetogram-open',
            page: '/reporting/hyetogram',
            tour: '/reporting',
            modes: ['page'],
            targetSelector: '.page-header',
            placement: 'pinned-bottom',
            nonBlocking: true,
            roles: [UserRole.ADMIN, UserRole.EXPERT_EN, UserRole.EXPERT_RP],
            title: t('tutorial.reportingHyetogramOpen.title'),
            content: t('tutorial.reportingHyetogramOpen.body'),
            navigateHint: t('tutorial.reportingHyetogramOpen.navigateHint'),
        },
        {
            id: 'reporting-processing-open',
            page: '/reporting/measurementsProcessing',
            tour: '/reporting',
            modes: ['page'],
            targetSelector: '.page-header',
            placement: 'pinned-bottom',
            nonBlocking: true,
            roles: [UserRole.ADMIN, UserRole.EXPERT_EN, UserRole.EXPERT_RP],
            title: t('tutorial.reportingProcessingOpen.title'),
            content: t('tutorial.reportingProcessingOpen.body'),
            navigateHint: t('tutorial.reportingProcessingOpen.navigateHint'),
        },
    ];

    const adminSteps: TutorialStep[] = [
        {
            id: 'admin-intro',
            page: '/admin',
            tour: '/admin',
            modes: ['page'],
            targetSelector: '.page-header',
            placement: 'pinned-bottom',
            nonBlocking: true,
            roles: [UserRole.ADMIN],
            title: t('tutorial.adminIntro.title'),
            content: t('tutorial.adminIntro.body'),
            navigateHint: t('tutorial.adminIntro.navigateHint'),
        },
        {
            id: 'admin-system',
            page: '/admin',
            tour: '/admin',
            modes: ['page'],
            targetSelector: '.tile-section--blue',
            placement: 'top',
            nonBlocking: true,
            roles: [UserRole.ADMIN],
            title: t('tutorial.adminSystem.title'),
            content: t('tutorial.adminSystem.body'),
            navigateHint: t('tutorial.adminSystem.navigateHint'),
        },
        {
            id: 'admin-stats-open',
            page: '/admin/system/stats',
            tour: '/admin',
            modes: ['page'],
            targetSelector: '.page-header',
            placement: 'pinned-bottom',
            nonBlocking: true,
            roles: [UserRole.ADMIN],
            title: t('tutorial.adminStatsOpen.title'),
            content: t('tutorial.adminStatsOpen.body'),
            navigateHint: t('tutorial.adminStatsOpen.navigateHint'),
        },
        {
            id: 'admin-heatmap-open',
            page: '/admin/heatmap',
            tour: '/admin',
            modes: ['page'],
            targetSelector: '.page-header',
            placement: 'pinned-bottom',
            nonBlocking: true,
            roles: [UserRole.ADMIN],
            title: t('tutorial.adminHeatmapOpen.title'),
            content: t('tutorial.adminHeatmapOpen.body'),
            navigateHint: t('tutorial.adminHeatmapOpen.navigateHint'),
        },
        {
            id: 'admin-data',
            page: '/admin',
            tour: '/admin',
            modes: ['page'],
            targetSelector: '.tile-section--green',
            placement: 'top',
            nonBlocking: true,
            roles: [UserRole.ADMIN],
            title: t('tutorial.adminData.title'),
            content: t('tutorial.adminData.body'),
            navigateHint: t('tutorial.adminData.navigateHint'),
        },
        {
            id: 'admin-nxcals-open',
            page: '/admin/system/nxcalsManagement',
            tour: '/admin',
            modes: ['page'],
            targetSelector: '.page-header',
            placement: 'pinned-bottom',
            nonBlocking: true,
            roles: [UserRole.ADMIN],
            title: t('tutorial.adminNxcalsOpen.title'),
            content: t('tutorial.adminNxcalsOpen.body'),
            navigateHint: t('tutorial.adminNxcalsOpen.navigateHint'),
        },
        {
            id: 'admin-export-open',
            page: '/admin/dataExportStatus',
            tour: '/admin',
            modes: ['page'],
            targetSelector: '.page-header',
            placement: 'pinned-bottom',
            nonBlocking: true,
            roles: [UserRole.ADMIN],
            title: t('tutorial.adminExportOpen.title'),
            content: t('tutorial.adminExportOpen.body'),
            navigateHint: t('tutorial.adminExportOpen.navigateHint'),
        },
    ];

    // Entity-page tour: every step lives on the pattern page, so the tour can only be
    // started from an open pipeline (the framework never navigates to pattern paths).
    const pipelineSteps: TutorialStep[] = [
        {
            id: 'pipeline-intro',
            page: PIPELINE_DETAILS_PAGE,
            modes: ['page'],
            targetSelector: '.top-pipeline-menu',
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.pipelineIntro.title'),
            content: t('tutorial.pipelineIntro.body'),
            navigateHint: t('tutorial.pipelineIntro.navigateHint'),
        },
        {
            id: 'pipeline-meta',
            page: PIPELINE_DETAILS_PAGE,
            modes: ['page'],
            targetSelector: '.top-pipeline-menu__meta',
            placement: 'bottom',
            nonBlocking: true,
            title: t('tutorial.pipelineMeta.title'),
            content: t('tutorial.pipelineMeta.body'),
            navigateHint: t('tutorial.pipelineMeta.navigateHint'),
        },
        {
            id: 'pipeline-time-range',
            page: PIPELINE_DETAILS_PAGE,
            modes: ['page'],
            targetSelector: '.top-pipeline-menu__date',
            placement: 'bottom',
            nonBlocking: true,
            title: t('tutorial.pipelineTimeRange.title'),
            content: t('tutorial.pipelineTimeRange.body'),
            navigateHint: t('tutorial.pipelineTimeRange.navigateHint'),
        },
        {
            id: 'pipeline-series',
            page: PIPELINE_DETAILS_PAGE,
            modes: ['page'],
            targetSelector: fl.gridRoot,
            placement: 'bottom',
            nonBlocking: true,
            title: t('tutorial.pipelineSeries.title'),
            content: t('tutorial.pipelineSeries.body'),
            navigateHint: t('tutorial.pipelineSeries.navigateHint'),
        },
        {
            id: 'pipeline-chart',
            page: PIPELINE_DETAILS_PAGE,
            modes: ['page'],
            targetSelector: '.trends-chart-pane',
            placement: 'pinned-bottom',
            nonBlocking: true,
            title: t('tutorial.pipelineChart.title'),
            content: t('tutorial.pipelineChart.body'),
            navigateHint: t('tutorial.pipelineChart.navigateHint'),
        },
        {
            id: 'pipeline-stages',
            page: PIPELINE_DETAILS_PAGE,
            modes: ['page'],
            targetSelector: '.pipeline-stages-wrapper',
            placement: 'top',
            nonBlocking: true,
            title: t('tutorial.pipelineStages.title'),
            content: t('tutorial.pipelineStages.body'),
            navigateHint: t('tutorial.pipelineStages.navigateHint'),
        },
        {
            id: 'pipeline-add-operation',
            page: PIPELINE_DETAILS_PAGE,
            modes: ['page'],
            targetSelector: '[data-tutorial="pipeline-add-operation"]',
            placement: 'top',
            nonBlocking: true,
            title: t('tutorial.pipelineAddOperation.title'),
            content: t('tutorial.pipelineAddOperation.body'),
            navigateHint: t('tutorial.pipelineAddOperation.navigateHint'),
        },
        {
            id: 'pipeline-save',
            page: PIPELINE_DETAILS_PAGE,
            modes: ['page'],
            targetSelector: '.top-pipeline-menu__group',
            placement: 'bottom',
            nonBlocking: true,
            title: t('tutorial.pipelineSave.title'),
            content: t('tutorial.pipelineSave.body'),
            navigateHint: t('tutorial.pipelineSave.navigateHint'),
        },
    ];

    return [
        ...overview,
        ...alarmsSteps,
        ...trendsSteps,
        ...notificationsSteps,
        ...monitoringGroupsSteps,
        ...dashboardsSteps,
        ...statisticsSteps,
        ...reportingSteps,
        ...pipelineSteps,
        ...adminSteps,
        ...applicationEditorSteps,
    ];
}

export function filterStepsByRole(steps: TutorialStep[], userRoles: UserRoleType[]): TutorialStep[] {
    return steps.filter((step) => !step.roles || step.roles.some((r) => userRoles.includes(r)));
}

/** Default modes when a step doesn't specify any. */
const DEFAULT_MODES: TutorialMode[] = ['full', 'page'];

/** Keep steps that participate in the requested mode. */
export function filterStepsByMode(steps: TutorialStep[], mode: TutorialMode): TutorialStep[] {
    return steps.filter((step) => (step.modes ?? DEFAULT_MODES).includes(mode));
}

/** True when this step lives on the given platform-relative path. */
export function isStepOnPage(step: TutorialStep, relativePath: string): boolean {
    return pathMatchesPage(step.page, relativePath);
}

/** The tour a step belongs to: explicit `tour`, else its own `page`. */
export function getStepTour(step: TutorialStep): string {
    return step.tour ?? step.page;
}

/**
 * Resolve which page-tour to start from a given relative path.
 * Priority: a tour whose id IS this path (hub root or standalone page); otherwise the tour
 * that owns this path as a subpage (a step with page === relativePath). Null if none.
 * Only considers 'page'-mode steps.
 */
export function resolveTourId(steps: TutorialStep[], relativePath: string): string | null {
    const pageSteps = filterStepsByMode(steps, 'page');
    // Return the tour's own id (a pattern path for entity pages), not the concrete path.
    const hubStep = pageSteps.find((s) => pathMatchesPage(getStepTour(s), relativePath));
    if (hubStep) return getStepTour(hubStep);
    const owner = pageSteps.find((s) => pathMatchesPage(s.page, relativePath));
    return owner ? getStepTour(owner) : null;
}

/** Steps belonging to a given tour id, in array order. */
export function filterStepsByTour(steps: TutorialStep[], tourId: string): TutorialStep[] {
    return steps.filter((s) => getStepTour(s) === tourId);
}

/** Steps belonging to the given platform-relative path. */
export function filterStepsByPage(steps: TutorialStep[], relativePath: string): TutorialStep[] {
    return steps.filter((step) => isStepOnPage(step, relativePath));
}

/** Translated label for the current page, used in the "<Page> tutorial" menu item. */
export function getCurrentPageLabel(t: TFunction, relativePath: string): string | null {
    const page = TUTORIAL_PAGES.find((p) => pathMatchesPage(p.path, relativePath));
    if (page) return t(page.labelKey);
    // Subpage: resolve to its owning hub tour and label the menu with the hub's name.
    const tourId = resolveTourId(getTutorialSteps(t), relativePath);
    const tourPage = tourId ? TUTORIAL_PAGES.find((p) => p.path === tourId) : undefined;
    return tourPage ? t(tourPage.labelKey) : null;
}

/**
 * Target for the "Go to {label}" wrong-page button. Resolves the step's `page` against the
 * registered TUTORIAL_PAGES for the i18n label, and re-attaches the active `:platform`
 * segment so navigation lands on the correct platform-scoped route.
 */
export function getNavTarget(
    t: TFunction,
    step: TutorialStep,
    platform: string | null | undefined
): {path: string; label: string} {
    const page = TUTORIAL_PAGES.find((p) => p.path === step.page);
    const base = platform ? `/${platform}` : '';
    const path = step.page === '/' ? base || '/' : `${base}${step.page}`;
    return {path, label: page ? t(page.labelKey) : step.page};
}

/**
 * Pages that have at least one 'page'-mode step, computed once at module load.
 * Building this from the steps list (rather than hard-coding) keeps it in sync as steps evolve.
 */
let pagesWithToursCache: Set<string> | null = null;
function getPagesWithTours(t: TFunction): Set<string> {
    if (pagesWithToursCache) return pagesWithToursCache;
    const pageSteps = getTutorialSteps(t).filter((s) => (s.modes ?? DEFAULT_MODES).includes('page'));
    const paths = new Set<string>();
    for (const s of pageSteps) {
        // The step's own page (covers hub roots and subpages)…
        paths.add(s.page);
        // …and its tour id, so hub roots are always present even with no step on the root.
        paths.add(getStepTour(s));
    }
    pagesWithToursCache = paths;
    return pagesWithToursCache;
}

/** True when the given platform-relative path has at least one step in 'page' mode. */
export function hasPageTutorialFor(t: TFunction, relativePath: string): boolean {
    const pages = getPagesWithTours(t);
    if (pages.has(relativePath)) return true;
    for (const page of pages) {
        if (isPatternPath(page) && pathMatchesPage(page, relativePath)) return true;
    }
    return false;
}
